import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import http from "@/lib/api";
import { Panel, Empty, StatusBadge, fmt } from "./AdminKit";
import ReplyPanel from "./ReplyPanel";

function ChatTranscript({ sid }) {
  const [msgs, setMsgs] = useState(null);
  const load = () => http.get(`/v1/admin/chat-sessions/${sid}`).then((r) => setMsgs(r.data.messages)).catch(() => setMsgs([]));
  if (!msgs) return <button onClick={load} className="mt-2 text-xs text-blue-300 hover:underline" data-testid={`chat-transcript-btn-${sid}`}>Показать переписку</button>;
  return (
    <div className="mt-2 space-y-1 max-h-72 overflow-y-auto bg-slate-900/50 rounded p-3 border border-slate-700" data-testid={`chat-transcript-${sid}`}>
      {msgs.map((m, i) => (
        <div key={i} className="text-xs"><span className={m.role === "user" ? "text-amber-300" : "text-blue-300"}>{m.role === "user" ? "Клиент" : "Vadim"}:</span> <span className="text-slate-300 whitespace-pre-wrap">{m.content}</span></div>
      ))}
    </div>
  );
}

function MechanicReply({ req, onSent }) {
  const [replies, setReplies] = useState(req.mechanic_replies || []);
  const send = async (body) => {
    const r = await http.post(`/v1/admin/ai-requests/${req.id}/send-reply`, body);
    setReplies((x) => [...x, r.data.reply]);
    onSent();
  };
  return (
    <div className="mt-3 space-y-2" data-testid={`mechanic-reply-${req.id}`}>
      {replies.map((x, i) => (
        <div key={i} className="text-xs rounded border border-emerald-800/60 bg-emerald-950/20 p-2" data-testid={`mechanic-reply-sent-${i}`}>
          <div className="text-emerald-300">Ответ механика (RU): <span className="text-slate-300">{x.text_ru}</span></div>
          <div className="text-blue-300 mt-1">Клиенту ({x.language?.toUpperCase()}): <span className="text-slate-300 whitespace-pre-wrap">{x.customer_text}</span></div>
        </div>
      ))}
      <ReplyPanel id={req.id} draftKey={`ai-${req.id}`} composeUrl={`/v1/admin/ai-requests/${req.id}/compose-reply`} onSend={send}
        placeholder={`Ответ механика по-русски — Vadim оформит ответ клиенту на его языке (${(req.language || "no").toUpperCase()})`}
        emailOption={req.email ? { default: true, label: `Также отправить на e-mail (${req.email})` } : null} />
    </div>
  );
}

export default function AdminAIRequests() {
  const [rows, setRows] = useState([]);
  const load = () => http.get("/v1/admin/ai-requests").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const close = async (id) => {
    try { await http.post(`/v1/admin/ai-requests/${id}/close`); toast.success("Запрос закрыт (запись сохранена)"); load(); } catch { toast.error("Ошибка"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-ai-requests">
      <h1 className="text-2xl font-bold text-white">AI-запросы (веб-ассистент)</h1>
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 text-slate-400 text-sm">
        Заявки, созданные клиентами через голосового/текстового AI-ассистента на сайте. Каждая заявка использует
        существующего клиента и создаёт ремонтный заказ (при наличии данных о машине). Резюме — на русском для механика.
      </div>
      <Panel title={`Всего: ${rows.length}`}>
        {rows.length === 0 ? <Empty /> : (
          <div className="space-y-4">
            {rows.map((r) => (
              <div key={r.id} data-testid={`ai-req-${r.id}`} className={`rounded-lg border p-4 ${r.status === "closed" ? "border-slate-700 bg-slate-800/40 opacity-70" : r.needs_human_follow_up ? "border-amber-600/60 bg-amber-950/20" : "border-slate-700 bg-slate-700/30"}`}>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-white">{r.customer_name}</span>
                    {r.job_code && (r.order_id
                      ? <Link to={`/admin/reparasjoner?open=${r.order_id}`} className="text-xs text-blue-300 underline" data-testid={`ai-req-job-link-${r.id}`}>{r.job_code}</Link>
                      : <span className="text-xs text-blue-300">{r.job_code}</span>)}
                    {r.customer_code && <Link to="/admin/kunder" className="text-xs text-slate-400 underline" data-testid={`ai-req-customer-link-${r.id}`}>{r.customer_code}</Link>}
                    <StatusBadge value={r.priority} />
                    {r.source === "vadim_chat" && <span className="px-2 py-0.5 rounded-full text-xs bg-blue-900/50 text-blue-300 border border-blue-700/50" data-testid={`ai-req-chat-badge-${r.id}`}>Веб-чат{r.language ? ` · ${r.language.toUpperCase()}` : ""}</span>}
                    {r.needs_human_follow_up && r.status !== "closed" && <span className="px-2 py-0.5 rounded-full text-xs bg-amber-900/50 text-amber-300 border border-amber-700/50">Требуется ответ</span>}
                    {r.status === "closed" && <span className="px-2 py-0.5 rounded-full text-xs bg-slate-700 text-slate-300" data-testid={`ai-req-closed-${r.id}`}>Закрыт {fmt(r.closed_at)}</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-500">{fmt(r.created_at)}</span>
                    {r.status !== "closed" && <button onClick={() => close(r.id)} data-testid={`ai-req-close-${r.id}`} className="px-2.5 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 text-xs">Закрыть</button>}
                  </div>
                </div>
                <div className="text-sm text-slate-300 mb-2">
                  Контакт: тел {r.phone || "—"} · email {r.email || "—"}
                  {(r.brand || r.model || r.machine_type) ? ` · Машина: ${[r.brand, r.model, r.machine_type].filter(Boolean).join(" ")}` : ""}
                </div>
                {r.summary_ru && <pre className="text-xs text-slate-300 whitespace-pre-wrap bg-slate-900/50 rounded p-3 border border-slate-700">{r.summary_ru}</pre>}
                {r.chat_session_id && <ChatTranscript sid={r.chat_session_id} />}
                {r.source === "vadim_chat" && <MechanicReply req={r} onSent={load} />}
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
